import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Container,
    Typography,
    Paper,
    Box,
    LinearProgress,
    CircularProgress,
    Alert,
} from "@mui/material";
import axios from "axios";
import { getAuthToken } from "../utils/authToken";

function UsageBlock({ title, usage }) {
    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{title} Characters Used</Typography>
            <Box mb={2}>
                <Typography>
                    {usage.character_count.toLocaleString()} / {usage.character_limit.toLocaleString()} characters
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={usage.character_limit ? (usage.character_count / usage.character_limit) * 100 : 0}
                    sx={{ height: 10, borderRadius: 5, mt: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {((usage.character_count / usage.character_limit) * 100).toFixed(2)}% used
                </Typography>
            </Box>
        </Box>
    );
}

function AdminAIUsagePage() {
    const [deepl, setDeepl] = useState(null);
    const [google, setGoogle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const tokenFromState = useSelector((state) => state.auth.token);
    const token = getAuthToken(tokenFromState);

    useEffect(() => {
        if (!token) {
            setDeepl(null);
            setGoogle(null);
            setError("Authentication token missing. Please sign in again.");
            setLoading(false);
            return;
        }
        const headers = { Authorization: `Bearer ${token}` };
        setLoading(true);
        setError("");
        Promise.all([
            axios.get("/api/deepl/admin/usage", { headers }),
            axios.get("/api/google/admin/usage", { headers }),
        ])
            .then(([dRes, gRes]) => {
                setDeepl(dRes.data);
                setGoogle(gRes.data);
            })
            .catch((err) => {
                setError(err.response?.data?.msg || "Failed to fetch usage.");
            })
            .finally(() => setLoading(false));
    }, [token]);

    return (
        <Container maxWidth="sm" sx={{ mt: 6 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                <Typography variant="h4" gutterBottom>
                    AI Usage (Admin)
                </Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <>
                        {deepl && <UsageBlock title="DeepL" usage={deepl} />}
                        {google && <UsageBlock title="Google" usage={google} />}
                        <Box mt={4}>
                            <Typography variant="body2" color="text.secondary">
                                Last refreshed: {new Date().toLocaleString()}
                            </Typography>
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
}

export default AdminAIUsagePage;
